<template>
  <div class="min-vh-100 bg-light">
    <!-- Header -->
    <header
      v-if="!isLoginPage"
      class="fixed-top bg-white shadow-sm"
      style="z-index: 1030"
    >
      <div class="container-fluid">
        <div class="d-flex justify-content-between align-items-center py-2">
          <div class="d-flex align-items-center">
            <h1 class="h4 mb-0 text-dark">5 OF 5</h1>
          </div>
          <div class="d-flex align-items-center">
            <!-- Mobile Sidebar Toggle Button -->

            <!-- Notifications Dropdown -->
            <div class="dropdown me-3">
              <button
                @click="toggleNotificationDropdown"
                class="btn btn-link text-dark text-decoration-none d-flex align-items-center position-relative"
                type="button"
              >
                <i class="bi bi-bell fs-4 text-dark"></i>
                <span
                  v-if="unreadNotifications > 0"
                  class="badge position-absolute top-2 start-90 translate-middle rounded-pill bg-danger"
                >
                  {{ unreadNotifications }}
                </span>
              </button>

              <!-- Notifications Dropdown Menu -->
              <div
                v-if="showNotificationDropdown"
                class="dropdown-menu show"
                :style="notificationDropdownStyle"
              >
                <div class="px-3 py-2 border-bottom">
                  <h6 class="mb-0">{{ $t("notifications") }}</h6>
                </div>
                <div
                  v-if="notifications.length === 0"
                  class="px-3 py-3 text-center text-muted"
                >
                  {{ $t("no_notifications") }}
                </div>
                <div v-else>
                  <button
                    v-for="notification in notifications"
                    :key="notification.id"
                    @click.prevent="handleNotificationClick(notification)"
                    class="dropdown-item d-flex align-items-start py-2"
                    :class="{ 'bg-light': !notification.read }"
                    style="cursor: pointer"
                  >
                    <div class="me-3">
                      <i
                        :class="notification.icon + ' fs-5'"
                        style="pointer-events: none"
                      ></i>
                    </div>
                    <div class="flex-grow-1" style="min-width: 0; max-width: 100%">
                      <div class="fw-bold text-truncate" style="pointer-events: none">
                        {{ notification.title }}
                      </div>
                      <div class="small text-muted">
                        <div
                          :class="{
                            'text-truncate-2-lines': !notification.showFullMessage,
                          }"
                          style="white-space: pre-wrap; word-break: break-word"
                          v-html="formatMessageWithLinks(notification.message)"
                        ></div>
                        <button
                          v-if="notification.message.length > 100"
                          @click.stop="toggleMessage(notification)"
                          class="btn btn-link btn-sm text-decoration-none p-0 mt-1"
                        >
                          {{ notification.showFullMessage ? "Show Less" : "View More" }}
                        </button>
                      </div>
                      <div class="small text-muted">
                        {{ formatTime(notification.date, notification.time) }}
                      </div>
                    </div>
                  </button>
                </div>
                <div
                  class="px-3 py-2 border-top d-flex justify-content-between align-items-center"
                >
                  <button
                    @click="markAllAsRead"
                    class="btn btn-link btn-sm text-decoration-none p-0"
                  >
                    {{ $t("mark_all_as_read") }}
                  </button>
                  <button
                    @click="viewAllNotifications"
                    class="btn btn-link btn-sm text-decoration-none p-0"
                  >
                    {{ $t("view_all") }}
                  </button>
                </div>
              </div>
            </div>

            <div class="dropdown">
              <button
                @click="toggleProfileDropdown"
                class="btn btn-link text-dark text-decoration-none d-flex align-items-center"
                type="button"
              >
                <i class="bi bi-person-circle fs-4 text-dark"></i>
              </button>

              <!-- Profile Dropdown -->
              <div
                v-if="showProfileDropdown"
                class="dropdown-menu show"
                :style="profileDropdownStyle"
              >
                <button
                  v-for="item in profileMenu"
                  :key="item.id"
                  @click="handleMenuAction(item.action)"
                  class="dropdown-item"
                >
                  {{ $t(item.title) }}
                </button>
              </div>
            </div>
            <button
              @click="toggleSidebar"
              class="btn btn-link text-dark text-decoration-none d-flex align-items-center me-2"
              type="button"
              v-if="!isDesktop"
            >
              <i class="fas fa-bars me-2"></i>
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Mobile Overlay -->
    <div
      v-if="isSidebarOpen && !isLoginPage"
      class="mobile-overlay"
      @click="closeSidebarOnMobile"
    ></div>

    <!-- Sidebar -->
    <aside
      v-if="!isLoginPage"
      class="sidebar fixed-top bg-white shadow-sm"
      :class="{ show: isSidebarOpen && !isDesktop }"
      :style="{
        top: '56px',
        bottom: '0',
        width: '250px',
        zIndex: '1035',
        [isRtl ? 'right' : 'left']: '0',
      }"
    >
      <nav class="p-3 h-100 overflow-auto">
        <ul class="nav flex-column">
          <li v-for="item in navigationItems" :key="item.id" class="nav-item">
            <!-- Regular navigation item -->
            <router-link
              v-if="!item.dropdown && !!item.sidebar"
              :to="item.path"
              class="nav-link d-flex align-items-center text-dark"
              :class="{ active: isActive(item.path)}"
              @click="closeSidebarOnMobile"
            >
              <i :class="item.icon + ' me-2'"></i>
              <span>{{ $t(item.title) }}</span>
            </router-link>

            <!-- Dropdown navigation item -->
            <div v-else-if="!!item.sidebar">
              <button
                @click="toggleDropdown(item.id)"
                class="nav-link d-flex align-items-center justify-content-between text-dark w-100 border-0 bg-transparent"
                :class="{ active: isDropdownActive(item) }"
              >
                <div class="d-flex align-items-center">
                  <i :class="item.icon + ' me-2'"></i>
                  <span>{{ item.title }}</span>
                </div>
                <i class="bi bi-chevron-down" :class="{ 'rotate-180': isDropdownOpen(item.id) }"></i>
              </button>

              <!-- Dropdown menu -->
              <ul v-if="isDropdownOpen(item.id)" class="nav flex-column ms-3 mt-2">
                <li v-for="child in item.children" :key="child.id" class="nav-item">
                  <router-link
                    :to="child.path"
                    class="nav-link d-flex align-items-center text-secondary py-2"
                    :class="{ active: isActive(child.path) }"
                    @click="closeSidebarOnMobile"
                  >
                    <i :class="child.icon + ' me-2'"></i>
                    <span>{{ child.title }}</span>
                  </router-link>
                </li>
              </ul>
            </div>
          </li>
          <!-- Language Switcher -->
          <li class="nav-item mt-3">
            <div class="nav-link d-flex align-items-center text-dark">
              <i class="fas fa-language me-2"></i>
              <select
                v-model="currentLocale"
                class="form-select form-select-sm"
                @change="changeLanguage"
              >
                <option value="en">{{ $t('common.english') }}</option>
                <option value="he">{{ $t('common.hebrew') }}</option>
              </select>
            </div>
          </li>
        </ul>
      </nav>
    </aside>

    <!-- Main Content -->
    <main
      class="main-content"
      :class="{ 'sidebar-open': isSidebarOpen, 'no-sidebar': isLoginPage }"
      :style="{
        paddingTop: '56px',
        transition: 'margin-left 0.3s ease-in-out, margin-right 0.3s ease-in-out'
      }"
    >
      <div class="container-fluid p-4">
        <router-view></router-view>
      </div>
    </main>
  </div>
</template>

<script>
import navigationItems from "@/navigation/navigationItems.js";
import profileMenu from "@/config/profileMenu.js";
import axios from "@/axios";
import { formatMessageWithLinks } from "@/Util/MessageFormatter";
import { mapGetters, mapState } from "vuex";

export default {
  name: "MainLayout",
  data() {
    return {
      authError: false,
      showProfileDropdown: false,
      showNotificationDropdown: false,
      isSidebarOpen: false,
      userName: "Admin User",
      navigationItems: [],
      profileMenu,
      notifications: [],
      unreadNotifications: 0,
      currentLocale: localStorage.getItem('user_language') || this.$i18n.locale,
      openDropdowns: []
    };
  },
  computed: {
    ...mapState(['locale']),
    ...mapGetters(['user']),
    isRtl() {
      return this.$i18n.locale === 'he';
    },
    isLoginPage() {
      return this.$route.meta.requiresAuth === false;
    },
    userRole() {
        return this.user?.role || "customer";
    },
    isDesktop() {
      return window.innerWidth >= 769;
    },
    notificationDropdownStyle() {
      // Determine if the current locale is RTL
      const isRtl = this.$i18n.locale === 'he';
      return {
        [isRtl ? 'left' : 'right']: '0',
        [isRtl ? 'right' : 'left']: 'auto',
        width: '350px',
        maxHeight: '400px',
        overflowY: 'auto',
        padding: '0'
      };
    },
    profileDropdownStyle() {
      const isRtl = this.$i18n.locale === 'he';
      return {
        [isRtl ? 'left' : 'right']: '0',
        [isRtl ? 'right' : 'left']: 'auto'
      };
    }
  },
  created() {
    // Set navigation items based on user role
    this.navigationItems = navigationItems[this.userRole] || navigationItems.user;

    // Set initial language and direction
    this.setLanguageAndDirection(this.currentLocale);
  },
  methods: {
    setLanguageAndDirection(locale) {
      this.$i18n.locale = locale;
      this.$store.dispatch('change_setting', locale);
      document.documentElement.lang = locale;
      document.documentElement.dir = locale === 'he' ? 'rtl' : 'ltr';
      localStorage.setItem('user_language', locale);
      this.$setDirection(locale);
    },
    changeLanguage() {
      this.setLanguageAndDirection(this.currentLocale);
    },
    formatMessageWithLinks,
    handleMenuAction(action) {
      if (typeof action === "function") {
        const isAdmin = ['super_admin', 'admin', 'brand_admin', 'account_admin'].includes(this.userRole);
        action({ router: this.$router, isAdmin });
        this.showProfileDropdown = false;
      }
    },
    toggleProfileDropdown() {
      this.showProfileDropdown = !this.showProfileDropdown;
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
      // Prevent body scroll when sidebar is open on mobile
      document.body.style.overflow = this.isSidebarOpen ? "hidden" : "";
    },
    closeSidebarOnMobile() {
      if (window.innerWidth < 768) {
        this.isSidebarOpen = false;
        document.body.style.overflow = "";
      }
    },
    isActive(path) {
      return this.$route.path === path;
    },
    toggleDropdown(itemId) {
      const index = this.openDropdowns.indexOf(itemId);
      if (index > -1) {
        this.openDropdowns.splice(index, 1);
      } else {
        this.openDropdowns.push(itemId);
      }
    },
    isDropdownOpen(itemId) {
      return this.openDropdowns.includes(itemId);
    },
    isDropdownActive(item) {
      if (!item.children) return false;
      return item.children.some(child => this.isActive(child.path));
    },
    toggleNotificationDropdown() {
      this.showNotificationDropdown = !this.showNotificationDropdown;
      if (this.showNotificationDropdown) {
        this.fetchNotifications();
      }
    },
    async fetchNotifications() {
      try {
        const response = await axios.get("/notifications");
        this.notifications = response.data.data.map((notification) => ({
          ...notification,
          icon: "bi bi-envelope", // Static mail icon
          title: "New Message", // Static title
          message: notification.message,
          date: notification.date,
          time: notification.time,
          read: notification.read || false,
          showFullMessage: false, // Add this property to track message expansion state
        }));
        this.unreadNotifications = this.notifications.filter((n) => !n.read).length;
      } catch (error) {
        console.error("Error fetching notifications:", error);
        // Fallback to empty array if API call fails
        this.notifications = [];
        this.unreadNotifications = 0;
      }
    },
    async handleNotificationClick(notification) {
      if (!notification.read) {
        try {
          await axios.put(`/notifications/${notification.id}/read`);
          notification.read = true;
          this.unreadNotifications--;
        } catch (error) {
          console.error("Error marking notification as read:", error);
        }
      }
      // Prevent any default behavior
      return false;
    },
    async markAllAsRead() {
      try {
        await axios.put("/notifications/mark-all-read");
        this.notifications.forEach((notification) => {
          notification.read = true;
        });
        this.unreadNotifications = 0;
      } catch (error) {
        console.error("Error marking all notifications as read:", error);
      }
    },
    formatTime(date, time) {
      // Format: "11.06.25" and "06:26"
      if (!date || !time) return "";

      // Convert date from "DD.MM.YY" to a more readable format
      const [day, month, year] = date.split(".");
      const formattedDate = `${day}/${month}/${year}`;

      return `${formattedDate} at ${time}`;
    },
    toggleMessage(notification) {
      notification.showFullMessage = !notification.showFullMessage;
    },
    viewAllNotifications() {
      // Close the dropdown
      this.showNotificationDropdown = false;
      // Navigate to notifications page
      this.$router.push({name:"adminNotifications"});
    },
  },
  mounted() {


    document.addEventListener("click", (e) => {
      if (!e.target.closest(".dropdown")) {
        this.showProfileDropdown = false;
        this.showNotificationDropdown = false;
      }
    });

    // Close sidebar when clicking outside on mobile
    document.addEventListener("click", (e) => {
      if (
        window.innerWidth < 768 &&
        !e.target.closest(".sidebar") &&
        !e.target.closest(".btn-link")
      ) {
        this.isSidebarOpen = false;
        document.body.style.overflow = "";
      }
    });

    // Handle window resize - only manage sidebar state for mobile
    window.addEventListener("resize", () => {
      // If resizing to a desktop view, ensure mobile sidebar is closed but don't affect desktop visibility
      if (this.isDesktop) {
        if (this.isSidebarOpen) {
          this.isSidebarOpen = false; // Only close if it was open from a mobile state
          document.body.style.overflow = "";
        }
      } else {
        // On mobile, if the overlay is clicked or sidebar is shown, ensure body overflow is handled
        document.body.style.overflow = this.isSidebarOpen ? "hidden" : "";
      }
    });
  },
  beforeUnmount() {
    // Clean up event listeners
    document.body.style.overflow = "";
  },
};
</script>

<style src="./MainLayout.css" scoped></style>
